import { motion } from "framer-motion";
import { Check, ChevronRight, Droplets, Sparkles } from "lucide-react";
import { PACKAGES, EXTRAS, SIZES } from "../data/pricing";

const PriceTable = ({ prices }) => (
  <div data-testid="size-price-table" className="grid grid-cols-4 gap-2 mt-5">
    {SIZES.map((s) => (
      <div
        key={s.id}
        className="rounded-lg border border-slate-500/20 bg-[#050B18]/50 py-3 text-center"
      >
        <div className="text-[10px] uppercase tracking-wider text-slate-400">
          {s.label}
        </div>
        <div className="text-lg font-bold text-[#A0E0FF] font-display mt-1">
          {prices[s.id]}€
        </div>
      </div>
    ))}
  </div>
);

const PackageCard = ({ pkg, index, onSelect }) => (
  <motion.article
    data-testid={`package-card-${pkg.id}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.55, delay: index * 0.12 }}
    className={`card-metallic rounded-3xl overflow-hidden flex flex-col relative ${
      pkg.badge ? "ring-1 ring-[#7EC8E3]/40" : ""
    }`}
  >
    {pkg.badge && (
      <div className="absolute top-4 right-4 z-10 rounded-full bg-gradient-to-r from-[#7EC8E3] to-[#A0E0FF] px-3 py-1 text-[11px] font-bold text-[#050B18] tracking-wide">
        {pkg.badge}
      </div>
    )}
    <div className="h-44 relative overflow-hidden">
      <img
        src={pkg.image}
        alt={pkg.name}
        className="w-full h-full object-cover opacity-70 hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B4A] to-transparent" />
      <div className="absolute bottom-4 left-5">
        <h3 className="text-2xl font-extrabold text-metallic font-display">
          {pkg.name}
        </h3>
        <p className="text-sm text-[#7EC8E3] font-medium">{pkg.subtitle}</p>
      </div>
    </div>

    <div className="p-6 flex flex-col flex-1">
      <p className="text-sm text-slate-300 leading-relaxed">{pkg.description}</p>

      <ul className="mt-5 space-y-2.5">
        {pkg.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-slate-200">
            <Check size={17} className="text-[#7EC8E3] mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        <div className="text-xs uppercase tracking-widest text-slate-400 mt-6 mb-1">
          Prezzi per dimensione
        </div>
        <PriceTable prices={pkg.prices} />

        <button
          data-testid={`btn-select-package-${pkg.id}`}
          onClick={() => onSelect(pkg.id)}
          className="btn-silver w-full rounded-full py-3.5 mt-6 inline-flex items-center justify-center gap-2 text-sm"
        >
          Aggiungi alla prenotazione <ChevronRight size={17} />
        </button>
      </div>
    </div>
  </motion.article>
);

const ExtraCard = ({ extra, index }) => (
  <motion.div
    data-testid={`extra-card-${extra.id}`}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className="card-metallic rounded-2xl p-5 flex gap-4 items-center"
  >
    <img
      src={extra.image}
      alt={extra.name}
      className="w-24 h-24 rounded-xl object-cover shrink-0"
    />
    <div>
      <h4 className="font-bold text-slate-100">{extra.name}</h4>
      <p className="text-xs text-slate-400 mt-1">{extra.description}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-300">
        {SIZES.map((s) => (
          <span key={s.id}>
            {s.label} <span className="text-[#A0E0FF] font-semibold">{extra.prices[s.id]}€</span>
          </span>
        ))}
      </div>
    </div>
  </motion.div>
);

export const Services = ({ onSelectPackage }) => {
  return (
    <section id="servizi" data-testid="services-section" className="relative py-24 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#7EC8E3] font-semibold">
            I nostri pacchetti
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3">
            Servizi e <span className="text-metallic">listino prezzi</span>
          </h2>
          <p className="text-slate-300 mt-4">
            Scegli il pacchetto perfetto per la tua auto. Prezzi trasparenti in
            base alla dimensione del veicolo.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-7 mt-14 max-w-4xl mx-auto">
          {PACKAGES.map((pkg, i) => (
            <PackageCard key={pkg.id} pkg={pkg} index={i} onSelect={onSelectPackage} />
          ))}
        </div>

        <div className="mt-16">
          <div className="text-center mb-8">
            <span className="text-xs uppercase tracking-[0.3em] text-[#7EC8E3] font-semibold">
              Servizi Extra
            </span>
            <h3 className="text-2xl sm:text-3xl font-bold mt-2">
              Aggiungi un <span className="text-metallic">tocco in più</span>
            </h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {EXTRAS.map((e, i) => (
              <ExtraCard key={e.id} extra={e} index={i} />
            ))}
          </div>

          <motion.div
            data-testid="coming-soon-card"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative max-w-4xl mx-auto mt-8 rounded-2xl border border-dashed border-[#7EC8E3]/50 bg-[#050B18]/60 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-5 overflow-hidden"
          >
            <div className="absolute top-4 right-4 rounded-full bg-gradient-to-r from-[#7EC8E3] to-[#A0E0FF] px-3 py-1 text-[11px] font-bold text-[#050B18] tracking-wide">
              PROSSIMAMENTE
            </div>
            <div className="w-16 h-16 shrink-0 rounded-2xl bg-gradient-to-br from-[#0D1B4A] to-[#0A1A3A] border border-[#7EC8E3]/40 flex items-center justify-center shadow-[0_0_18px_rgba(126,200,227,0.25)]">
              <Droplets size={28} className="text-[#A0E0FF]" />
            </div>
            <div className="text-center sm:text-left">
              <h4 className="text-xl font-bold text-slate-100 flex items-center gap-2 justify-center sm:justify-start">
                Lavaggi e trattamenti esterni <Sparkles size={18} className="text-[#7EC8E3]" />
              </h4>
              <p className="text-sm text-slate-400 mt-2">
                Presto disponibili anche lavaggio carrozzeria, decontaminazione, lucidatura e protezione della
                vernice. Sempre a domicilio, con la stessa cura del dettaglio. Seguici su Instagram per le novità.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
